import React, { useState, useEffect } from 'react';
import { CheckCircle, Link2, Loader2, Search, Trash2, Unlink, Upload, X } from 'lucide-react';
import { api } from '../../api';
import { TagInput } from '../TagInput';

export const EditCourseModal = ({ course, onClose, onSuccess }) => {
  const [metadata, setMetadata] = useState(null);
  const [formData, setFormData] = useState({
    title: course.title || '',
    description: course.description || '',
    solution_area: course.solution_area || '',
    course_type: course.course_type || '',
    level: course.level || '',
    language: course.language || '',
    target_audience: course.target_audience || '',
    duration: course.duration || '',
    search_tags: course.search_tags || [],
    hands_on_lab: !!course.hands_on_lab,
    certification_course: !!course.certification_course,
    catalog_course_id: course.catalog_course_id || '',
    course_code: course.course_code || '',
  });
  const [files, setFiles] = useState(course.files || []);
  const [uploadingType, setUploadingType] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Catalog linking
  const [catalogCourses, setCatalogCourses] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogWarning, setCatalogWarning] = useState('');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [showCatalogDropdown, setShowCatalogDropdown] = useState(false);

  useEffect(() => {
    api.request('/metadata').then(setMetadata).catch(() => {});
    setCatalogLoading(true);
    api.request('/catalog/courses')
      .then(data => { setCatalogCourses(data.courses || []); setCatalogWarning(data.warning || ''); })
      .catch(err => setCatalogWarning(err.message || 'Could not load catalog.'))
      .finally(() => setCatalogLoading(false));
  }, []);

  const isLinked = !!formData.catalog_course_id;
  const linkedCourse = isLinked ? catalogCourses.find(c => c.catalog_course_id === formData.catalog_course_id) : null;

  const filteredCatalog = catalogSearch.trim()
    ? catalogCourses.filter(c => (c.title || '').toLowerCase().includes(catalogSearch.toLowerCase())).slice(0, 15)
    : [];

  const handleLinkCatalogCourse = (c) => {
    setFormData(prev => ({
      ...prev,
      catalog_course_id: c.catalog_course_id,
      course_code: c.course_code || '',
      solution_area: c.solution_area || prev.solution_area,
      course_type: c.course_type || prev.course_type,
      level: c.level || prev.level,
      target_audience: c.target_audience || prev.target_audience,
      duration: c.duration || prev.duration,
    }));
    setCatalogSearch('');
    setShowCatalogDropdown(false);
  };

  const handleUnlink = () => {
    setFormData(prev => ({ ...prev, catalog_course_id: '', course_code: '' }));
  };

  const handleField = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await api.request(`/courses/${course.id}/files/${fileId}`, { method: 'DELETE' });
      setFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (err) {
      setError(err.message || 'Delete failed.');
    }
  };

  const handleAddFile = async (fileType, file) => {
    if (!file) return;
    setUploadingType(fileType);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('file_type', fileType);
      await api.uploadFile(`/courses/${course.id}/files`, fd, () => {});
      const data = await api.request(`/courses/${course.id}`);
      setFiles(data.files || []);
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setUploadingType(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await api.request(`/courses/${course.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...formData,
          unlink_catalog: !formData.catalog_course_id,
        })
      });
      onSuccess();
    } catch (err) {
      setError(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-course-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-levelup-dark">Edit Course</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="edit-course-close"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-5">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="edit-course-error">{error}</div>}

          {/* Catalog Link */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-levelup-dark uppercase tracking-wide mb-2">Catalog Link</h3>
            {isLinked ? (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Link2 className="text-green-600 flex-shrink-0" size={16} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-green-800 truncate">{linkedCourse?.title || formData.course_code}</p>
                    <p className="text-xs text-green-600">Course ID: {formData.catalog_course_id}</p>
                  </div>
                </div>
                <button type="button" onClick={handleUnlink} className="flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700 flex-shrink-0" data-testid="unlink-catalog-button">
                  <Unlink size={14} /> Unlink
                </button>
              </div>
            ) : (
              <div className="relative">
                {catalogLoading ? (
                  <p className="text-sm text-gray-400 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading catalog…</p>
                ) : catalogWarning ? (
                  <p className="text-sm text-amber-600">{catalogWarning}</p>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                      <input
                        type="text"
                        value={catalogSearch}
                        onChange={e => { setCatalogSearch(e.target.value); setShowCatalogDropdown(true); }}
                        onFocus={() => setShowCatalogDropdown(true)}
                        placeholder="Search catalog courses to link…"
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-levelup-accent"
                        data-testid="catalog-link-search-input"
                      />
                    </div>
                    {showCatalogDropdown && filteredCatalog.length > 0 && (
                      <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                        {filteredCatalog.map(c => (
                          <button key={c.catalog_course_id} type="button"
                            onClick={() => handleLinkCatalogCourse(c)}
                            className="w-full text-left px-3 py-2 hover:bg-levelup-accent/10 text-sm border-b border-gray-50 last:border-0"
                            data-testid={`catalog-link-option-${c.catalog_course_id}`}>
                            <p className="font-medium text-gray-800 truncate">{c.title}</p>
                            <p className="text-xs text-gray-400">{c.course_code} · {c.solution_area}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Core fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Title</label>
              <input name="title" value={formData.title} onChange={handleField} className="input-field" data-testid="edit-course-title" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Description</label>
              <textarea name="description" value={formData.description} onChange={handleField} rows={3} className="input-field" data-testid="edit-course-description" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Solution Area</label>
              <select name="solution_area" value={formData.solution_area} onChange={handleField} className="input-field" data-testid="edit-course-solution-area">
                <option value="">—</option>
                {metadata?.solution_areas?.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Course Type</label>
              <select name="course_type" value={formData.course_type} onChange={handleField} className="input-field" data-testid="edit-course-type">
                <option value="">—</option>
                {metadata?.course_types?.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Level</label>
              <select name="level" value={formData.level} onChange={handleField} className="input-field" data-testid="edit-course-level">
                <option value="">—</option>
                {metadata?.levels?.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Language</label>
              <select name="language" value={formData.language} onChange={handleField} className="input-field" data-testid="edit-course-language">
                <option value="">—</option>
                {metadata?.languages?.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Target Audience</label>
              <select name="target_audience" value={formData.target_audience} onChange={handleField} className="input-field" data-testid="edit-course-target-audience">
                <option value="">—</option>
                {metadata?.target_audiences?.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Duration</label>
              <input name="duration" value={formData.duration} onChange={handleField} className="input-field" data-testid="edit-course-duration" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Search Tags</label>
              <TagInput value={formData.search_tags} onChange={(tags) => setFormData(prev => ({ ...prev, search_tags: tags }))} />
            </div>
            <div className="flex items-center gap-4 md:col-span-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" name="hands_on_lab" checked={formData.hands_on_lab} onChange={handleField} data-testid="edit-course-hands-on-lab" />
                Hands-on Lab Available
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" name="certification_course" checked={formData.certification_course} onChange={handleField} data-testid="edit-course-certification" />
                Certification Course
              </label>
            </div>
          </div>

          {/* File Management */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-levelup-dark uppercase tracking-wide mb-3">Files ({files.length})</h3>
            {files.length > 0 && (
              <div className="space-y-2 mb-3">
                {files.map(f => (
                  <div key={f.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2" data-testid={`edit-course-file-${f.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-semibold text-levelup-accent bg-levelup-accent/10 rounded px-2 py-0.5 flex-shrink-0">{f.file_type}</span>
                      <span className="text-sm text-gray-700 truncate">{f.original_name}</span>
                    </div>
                    <button type="button" onClick={() => handleDeleteFile(f.id)} className="text-red-400 hover:text-red-600 flex-shrink-0" data-testid={`edit-course-delete-file-${f.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {metadata?.file_types?.map(ft => (
                <label key={ft} className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-lg p-3 hover:border-levelup-accent cursor-pointer text-center">
                  {uploadingType === ft ? (
                    <Loader2 className="animate-spin text-levelup-accent" size={18} />
                  ) : (
                    <Upload className="text-gray-300" size={18} />
                  )}
                  <span className="text-xs text-gray-500 mt-1">{ft}</span>
                  <input type="file" className="hidden" disabled={uploadingType !== null}
                    onChange={e => handleAddFile(ft, e.target.files[0])}
                    data-testid={`edit-course-add-file-${ft.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`} />
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-5 border-t sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium" data-testid="edit-course-cancel">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-6 py-2 bg-levelup-accent text-levelup-darker rounded-lg font-semibold hover:bg-levelup-accent-hover disabled:opacity-50 flex items-center gap-2"
            data-testid="edit-course-save">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle size={16} />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};
